import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Network, Plus, Target, Users } from 'lucide-react';

interface OrgNode {
  id: string;
  email: string;
  role: string;
  reportsTo: string | null;
}

interface OrgTargetRow {
  id: string;
  scope: string;
  periodStart: string;
  periodEnd: string;
  targetAmount: number;
  currency: string;
}

interface CreateTargetRequest {
  ownerUserId: string | null;
  scope: string;
  periodStart: string;
  periodEnd: string;
  targetAmount: number;
  currency: string;
}

interface TreeNode {
  node: OrgNode;
  children: TreeNode[];
}

function buildTree(nodes: OrgNode[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  for (const node of nodes) {
    map.set(node.id, { node, children: [] });
  }
  const roots: TreeNode[] = [];
  for (const node of nodes) {
    const treeNode = map.get(node.id)!;
    if (node.reportsTo && map.has(node.reportsTo)) {
      map.get(node.reportsTo)!.children.push(treeNode);
    } else {
      roots.push(treeNode);
    }
  }
  return roots;
}

const ROLE_BADGE: Record<string, string> = {
  PLATFORM_ADMIN: 'bg-purple-900/40 text-purple-200',
  TENANT_ADMIN: 'bg-indigo-900/40 text-indigo-200',
  SALES_MANAGER: 'bg-blue-900/40 text-blue-200',
  SALES_REP: 'bg-emerald-900/40 text-emerald-200',
};

function TreeNodeView({ treeNode, depth }: { treeNode: TreeNode; depth: number }) {
  const badgeClass = ROLE_BADGE[treeNode.node.role] ?? 'bg-muted text-foreground';
  return (
    <div>
      <div
        className="flex items-center gap-2 py-1"
        style={{ paddingLeft: `${depth * 24}px` }}
      >
        {depth > 0 && <span className="text-muted-foreground">---</span>}
        <span className="text-sm">{treeNode.node.email}</span>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${badgeClass}`}
        >
          {treeNode.node.role}
        </span>
      </div>
      {treeNode.children.map((child) => (
        <TreeNodeView key={child.node.id} treeNode={child} depth={depth + 1} />
      ))}
    </div>
  );
}

export function OrgPage() {
  const { t } = useTranslation();
  const [orgNodes, setOrgNodes] = useState<OrgNode[]>([]);
  const [targets, setTargets] = useState<OrgTargetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create target form state
  const [showForm, setShowForm] = useState(false);
  const [formScope, setFormScope] = useState('TEAM');
  const [formOwner, setFormOwner] = useState('');
  const [formPeriodStart, setFormPeriodStart] = useState('');
  const [formPeriodEnd, setFormPeriodEnd] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formCurrency, setFormCurrency] = useState('USD');
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [treeRes, targetsRes] = await Promise.all([
        apiClient.get<OrgNode[]>('/users/org-tree'),
        apiClient.get<OrgTargetRow[]>('/analytics/org-targets'),
      ]);
      setOrgNodes(treeRes.data);
      setTargets(targetsRes.data);
    } catch {
      setError('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreateTarget() {
    setFormError(null);
    if (!formPeriodStart || !formPeriodEnd || !formAmount) {
      setFormError('Period and amount are required');
      return;
    }
    try {
      const body: CreateTargetRequest = {
        ownerUserId: formOwner || null,
        scope: formScope,
        periodStart: formPeriodStart,
        periodEnd: formPeriodEnd,
        targetAmount: parseFloat(formAmount),
        currency: formCurrency,
      };
      await apiClient.post('/analytics/org-targets', body);
      setShowForm(false);
      setFormOwner('');
      setFormPeriodStart('');
      setFormPeriodEnd('');
      setFormAmount('');
      setFormCurrency('USD');
      load();
    } catch {
      setFormError('Failed to create target');
    }
  }

  const tree = buildTree(orgNodes);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Organization</h1>
        <Button asChild size="sm" variant="outline">
          <Link to="/admin/users">
            <Users className="h-4 w-4" />
            {t('org.manage_users')}
          </Link>
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Org Tree Section */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Network className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-medium">Org Tree</h2>
        </div>
        <div className="rounded-lg border bg-card p-4">
          {tree.length === 0 ? (
            <p className="text-sm text-muted-foreground">No organization data available</p>
          ) : (
            tree.map((root) => (
              <TreeNodeView key={root.node.id} treeNode={root} depth={0} />
            ))
          )}
        </div>
      </section>

      {/* Targets Section */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-medium">Targets</h2>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowForm((v) => !v)}>
            <Plus className="h-4 w-4" />
            {showForm ? 'Cancel' : 'New Target'}
          </Button>
        </div>

        {/* Create Target Form */}
        {showForm && (
          <div className="rounded-lg border bg-card p-4 space-y-3">
            <h3 className="text-sm font-medium">Create Target</h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Scope</label>
                <select
                  value={formScope}
                  onChange={(e) => setFormScope(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="TEAM">TEAM</option>
                  <option value="ORG">ORG</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Owner</label>
                <select
                  value={formOwner}
                  onChange={(e) => setFormOwner(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">-- unassigned --</option>
                  {orgNodes.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Period Start</label>
                <Input
                  type="date"
                  value={formPeriodStart}
                  onChange={(e) => setFormPeriodStart(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Period End</label>
                <Input
                  type="date"
                  value={formPeriodEnd}
                  onChange={(e) => setFormPeriodEnd(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Target Amount</label>
                <Input
                  type="number"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  placeholder="0.00"
                  min={0}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Currency</label>
                <Input
                  value={formCurrency}
                  onChange={(e) => setFormCurrency(e.target.value)}
                  placeholder="USD"
                />
              </div>
            </div>
            {formError && <p className="text-xs text-destructive">{formError}</p>}
            <Button size="sm" onClick={handleCreateTarget}>
              Create
            </Button>
          </div>
        )}

        {/* Targets Table */}
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Scope</th>
                <th className="px-3 py-2">Period</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2">Currency</th>
              </tr>
            </thead>
            <tbody>
              {targets.map((t) => (
                <tr key={t.id} className="border-t">
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        t.scope === 'ORG'
                          ? 'bg-indigo-900/40 text-indigo-200'
                          : 'bg-blue-900/40 text-blue-200'
                      }`}
                    >
                      {t.scope}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {t.periodStart} to {t.periodEnd}
                  </td>
                  <td className="px-3 py-2 font-medium">
                    {Number(t.targetAmount).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">{t.currency}</td>
                </tr>
              ))}
              {targets.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                    No targets defined
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
